import { useEffect, useState } from 'react'
import { Wrapper } from 'components'
import { useParams } from 'react-router-dom'
import { Button, Card, Spinner, toast } from 'keep-react'
import { FloppyDisk } from 'phosphor-react'
import { simService } from 'api/services/sim'
import {
  GetSimulatorResponse,
  SimulatorOrderConfig,
  SimulatorOrderedTreeNode,
  SimulatorProduct,
  SimulatorRiskGroupOrderItem,
  SimulatorTopLevelOrderItem,
  SimulatorSavePayload,
} from 'api/types/simulator'
import { GetSimConfigResponse } from 'api/types/simConfig'
import { SortableSimulator } from './SortableSimulator'
import { simConfigService } from 'api/services/simConfig'

const isSimulatorProduct = (value: unknown): value is SimulatorProduct => {
  if (!value || typeof value !== 'object') return false
  const candidate = value as SimulatorProduct
  return (
    typeof candidate.ProductCode === 'string' &&
    Array.isArray(candidate.Questions) &&
    Array.isArray(candidate.Risks)
  )
}

const getOrderConfigFromOrderedTree = (
  orderedTree: SimulatorOrderedTreeNode[],
): SimulatorOrderConfig => {
  const topLevel: SimulatorTopLevelOrderItem[] = []
  const riskGroups: Record<string, SimulatorRiskGroupOrderItem[]> = {}

  orderedTree.forEach(node => {
    if (node.type === 'top-questions') {
      topLevel.push('questions')
      return
    }

    if (node.type !== 'risk' || !node.code) return

    topLevel.push(`risk:${node.code}` as SimulatorTopLevelOrderItem)

    const groups = (node.children ?? []).reduce<SimulatorRiskGroupOrderItem[]>(
      (acc, child) => {
        if (child.type === 'risk-questions') acc.push('questions')
        if (child.type === 'risk-covers') acc.push('covers')
        return acc
      },
      [],
    )

    if (groups.length > 0) {
      riskGroups[node.code] = groups
    }
  })

  return { topLevel, riskGroups }
}

const getSimulatorFromOrderedTree = (
  orderedTree: SimulatorOrderedTreeNode[],
): SimulatorProduct | null => {
  const root = orderedTree.find(
    node => node.type === 'top-questions' && isSimulatorProduct(node.data),
  )

  return root && isSimulatorProduct(root.data) ? root.data : null
}

const parseJsonIfString = (value: unknown): unknown => {
  if (typeof value !== 'string') return value
  try {
    return JSON.parse(value)
  } catch {
    return null
  }
}

const resolveSavedPayload = (
  response: GetSimConfigResponse,
): SimulatorSavePayload | null => {
  const parsedContent = parseJsonIfString(response.content)
  if (!parsedContent) return null

  const payloadCandidate =
    typeof parsedContent === 'object'
      ? (parsedContent as Partial<SimulatorSavePayload>)
      : null

  const orderedTree = Array.isArray(parsedContent)
    ? (parsedContent as SimulatorOrderedTreeNode[])
    : Array.isArray(payloadCandidate?.orderedTree)
      ? payloadCandidate?.orderedTree
      : null
  if (!orderedTree) return null

  const simulator = isSimulatorProduct(payloadCandidate?.simulator)
    ? payloadCandidate?.simulator
    : getSimulatorFromOrderedTree(orderedTree)
  if (!simulator) return null

  const orderConfig =
    payloadCandidate?.orderConfig &&
    Array.isArray(payloadCandidate.orderConfig.topLevel) &&
    payloadCandidate.orderConfig.riskGroups
      ? payloadCandidate.orderConfig
      : getOrderConfigFromOrderedTree(orderedTree)

  return {
    simulator,
    orderConfig,
    orderedTree,
  }
}

const getDefaultOrderConfig = (
  simulator: SimulatorProduct,
): SimulatorOrderConfig => ({
  topLevel: [
    'questions',
    ...simulator.Risks.map(risk => `risk:${risk.RiskCode}` as const),
  ],
  riskGroups: Object.fromEntries(
    simulator.Risks.map(risk => [
      risk.RiskCode,
      [
        ...(risk.Questions.length > 0 ? (['questions'] as const) : []),
        ...(risk.Covers.length > 0 ? (['covers'] as const) : []),
      ],
    ]),
  ),
})

const getSavePayload = (
  simulator: SimulatorProduct,
  orderConfig: SimulatorOrderConfig,
): SimulatorSavePayload => ({
  simulator,
  orderConfig,
  orderedTree: getOrderedTree(simulator, orderConfig),
})

const getQuestionNodes = (
  questions: SimulatorProduct['Questions'],
  parentId: string,
): SimulatorOrderedTreeNode[] =>
  questions.map(question => ({
    id: `${parentId}__question__${question.InputId}`,
    type: 'question',
    code: question.QuestionCode,
    label: question.QuestionText,
    attribute: question.Attribute,
    data: question,
  }))

const getCoverNode = (
  riskCode: string,
  cover: SimulatorProduct['Risks'][number]['Covers'][number],
): SimulatorOrderedTreeNode => ({
  id: `risk:${riskCode}__cover:${cover.CoverCode}`,
  type: 'cover',
  code: cover.CoverCode,
  label: cover.Description,
  data: cover,
  children: [
    ...(cover.Questions.length > 0
      ? [
          {
            id: `risk:${riskCode}__cover:${cover.CoverCode}__questions`,
            type: 'cover-questions' as const,
            children: getQuestionNodes(
              cover.Questions,
              `risk:${riskCode}__cover:${cover.CoverCode}`,
            ),
          },
        ]
      : []),
    ...(cover.Items.length > 0
      ? [
          {
            id: `risk:${riskCode}__cover:${cover.CoverCode}__items`,
            type: 'cover-items' as const,
            children: cover.Items.map(item => ({
              id: `risk:${riskCode}__cover:${cover.CoverCode}__item:${item.ItemCode}`,
              type: 'item' as const,
              code: item.ItemCode,
              label: item.Description,
              data: item,
              children:
                item.Questions.length > 0
                  ? [
                      {
                        id: `risk:${riskCode}__cover:${cover.CoverCode}__item:${item.ItemCode}__questions`,
                        type: 'item-questions' as const,
                        children: getQuestionNodes(
                          item.Questions,
                          `risk:${riskCode}__cover:${cover.CoverCode}__item:${item.ItemCode}`,
                        ),
                      },
                    ]
                  : [],
            })),
          },
        ]
      : []),
  ],
})

const getRiskNode = (
  risk: SimulatorProduct['Risks'][number],
  orderConfig: SimulatorOrderConfig,
): SimulatorOrderedTreeNode => {
  const groupOrder = orderConfig.riskGroups[risk.RiskCode] ?? [
    ...(risk.Questions.length > 0 ? (['questions'] as const) : []),
    ...(risk.Covers.length > 0 ? (['covers'] as const) : []),
  ]

  return {
    id: `risk:${risk.RiskCode}`,
    type: 'risk',
    code: risk.RiskCode,
    label: risk.Description,
    data: risk,
    children: groupOrder.reduce<SimulatorOrderedTreeNode[]>((nodes, group) => {
      if (group === 'questions' && risk.Questions.length > 0) {
        nodes.push({
          id: `risk:${risk.RiskCode}__questions`,
          type: 'risk-questions',
          children: getQuestionNodes(risk.Questions, `risk:${risk.RiskCode}`),
        })
      }

      if (group === 'covers' && risk.Covers.length > 0) {
        nodes.push({
          id: `risk:${risk.RiskCode}__covers`,
          type: 'risk-covers',
          children: risk.Covers.map(cover =>
            getCoverNode(risk.RiskCode, cover),
          ),
        })
      }

      return nodes
    }, []),
  }
}

const getOrderedTree = (
  simulator: SimulatorProduct,
  orderConfig: SimulatorOrderConfig,
): SimulatorOrderedTreeNode[] =>
  orderConfig.topLevel.flatMap(item => {
    if (item === 'questions') {
      return [
        {
          id: 'top__questions',
          type: 'top-questions' as const,
          data: simulator,
          children: getQuestionNodes(simulator.Questions, 'top'),
        },
      ]
    }

    const riskCode = item.replace('risk:', '')
    const risk = simulator.Risks.find(
      currentRisk => currentRisk.RiskCode === riskCode,
    )

    return risk ? [getRiskNode(risk, orderConfig)] : []
  })

const getOrderSnapshot = (payload: SimulatorSavePayload) => ({
  productCode: payload.simulator.ProductCode,
  topLevel: payload.orderConfig.topLevel,
  riskGroups: payload.orderConfig.riskGroups,
  orderedTree: payload.orderedTree,
  topLevelQuestions: payload.simulator.Questions.map(
    question => question.QuestionCode,
  ),
  risks: payload.simulator.Risks.map(risk => ({
    riskCode: risk.RiskCode,
    questions: risk.Questions.map(question => question.QuestionCode),
    covers: risk.Covers.map(cover => ({
      coverCode: cover.CoverCode,
      questions: cover.Questions.map(question => question.QuestionCode),
      items: cover.Items.map(item => ({
        itemCode: item.ItemCode,
        questions: item.Questions.map(question => question.QuestionCode),
      })),
    })),
  })),
})

export const BackOfficeSimulator = () => {
  const { productCode } = useParams<{ productCode: string }>()
  const [simId, setSimId] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [simulator, setSimulator] = useState<SimulatorProduct | null>(null)
  const [orderConfig, setOrderConfig] = useState<SimulatorOrderConfig | null>(
    null,
  )

  const getSimulator = async () => {
    if (!productCode) return
    setIsLoading(true)

    try {
      let savedPayload: SimulatorSavePayload | null = null

      try {
        const configResponse = (await simConfigService.getSimConfig(
          productCode,
        )) as GetSimConfigResponse
        setSimId(configResponse.id)
        savedPayload = resolveSavedPayload(configResponse)
      } catch {
        setSimId(null)
        savedPayload = null
      }

      if (savedPayload) {
        setSimulator(savedPayload.simulator)
        setOrderConfig(savedPayload.orderConfig)
        return
      }

      const response = (await simService.getSimulator(
        productCode,
      )) as GetSimulatorResponse
      setSimId(null)
      setSimulator(response.Product[0])
      setOrderConfig(getDefaultOrderConfig(response.Product[0]))
    } catch (error: any) {
      toast.error('Erro ao carregar o produto')
    } finally {
      setIsLoading(false)
    }
  }

  const saveOrder = async () => {
    if (!simulator) return
    const payload = getSavePayload(
      simulator,
      orderConfig ?? getDefaultOrderConfig(simulator),
    )
    const orderedPayload = payload.orderedTree
    console.log('save order snapshot', getOrderSnapshot(payload))
    console.log('save payload', JSON.stringify(orderedPayload, null, 2))
    try {
      setIsSaving(true)
      const response = await simConfigService.saveSimConfig(
        simId ?? null,
        simulator.ProductCode,
        JSON.stringify(orderedPayload),
        null,
      )
      setSimId(typeof response === 'string' ? parseInt(response) : null)
      toast.success('Ordem gravada com sucesso.')
    } catch {
      toast.error('Erro ao gravar a ordem.')
    } finally {
      setIsSaving(false)
    }
  }

  useEffect(() => {
    getSimulator()
  }, [productCode])

  return (
    <div className="w-full flex-1 py-12 overflow-auto">
      <Wrapper>
        {isLoading ? (
          <Card className="p-4 w-full max-w-full">
            <Spinner color="primary" className="mx-auto" />
          </Card>
        ) : !simulator ? (
          <Card className="p-4 w-full max-w-full">
            <div className="text-heading-6 text-metal-400">
              Produto indisponível
            </div>
          </Card>
        ) : (
          <>
            <h1 className="text-xxxl font-medium text-primary-600 pb-6 flex items-center justify-between">
              {simulator.Description}
              <Button
                color="secondary"
                variant="default"
                disabled={isSaving}
                className="gap-2"
                onClick={saveOrder}
              >
                <FloppyDisk size={16} />
                Gravar ordem
              </Button>
            </h1>
            <SortableSimulator
              simulator={simulator}
              onChange={updated => {
                setSimulator(updated)
              }}
              onOrderConfigChange={updated => setOrderConfig(updated)}
            />
          </>
        )}
      </Wrapper>
    </div>
  )
}
